
import { Field, Input, Stack, Heading, Text } from '@client/shared/design';
import { firstFieldError } from '@client/shared/form';
import { addDays, todayInZone } from '@client/shared/lib/wall-clock-intake.ts';
import {
  DEFAULT_BOOKING_HORIZON_DAYS,
  MIN_BOOKING_HORIZON_DAYS,
  MAX_BOOKING_HORIZON_DAYS,
} from '@shared/listing';
import { RadioCard } from './RadioCard.tsx';
import { UnavailabilityCalendar } from './UnavailabilityCalendar.tsx';
import { BookingWindowMode, type NewListingFormApi } from './use-new-listing-form.ts';
import styles from './NewListing.module.css';

export function StepAvailability({
  form,
  showBlocks = true,
}: {
  form: NewListingFormApi;
  showBlocks?: boolean;
}) {
  return (
    <Stack gap={6}>
      <Stack gap={1}>
        <Heading level={3}>When can agents book it?</Heading>
        <Text tone="soft">
          {showBlocks
            ? "Agents can book any open 30-minute slot within the booking window. Block out times the property can’t host showings."
            : "Agents can book any open 30-minute slot within the booking window. Blocked time is managed from the listing’s calendar."}
        </Text>
      </Stack>

      <form.Field name="bookingWindowMode">
        {(modeField) => {
          const mode = modeField.state.value;
          return (
            <Stack gap={4}>
              <Field label="Booking window">
                {() => (
                  <Stack gap={3}>
                    <RadioCard
                      name="bookingWindowMode"
                      checked={mode === BookingWindowMode.Date}
                      onChange={() => modeField.handleChange(BookingWindowMode.Date)}
                      title="Until a specific date"
                      desc="A fixed last day: agents can’t book later than the selected date."
                    />
                    <RadioCard
                      name="bookingWindowMode"
                      checked={mode === BookingWindowMode.Days}
                      onChange={() => modeField.handleChange(BookingWindowMode.Days)}
                      title="How many days ahead"
                      desc="A rolling window: agents can always book this many days out from today."
                    />
                  </Stack>
                )}
              </Field>

              {mode === BookingWindowMode.Days ? <DaysField form={form} /> : <DateField form={form} />}
            </Stack>
          );
        }}
      </form.Field>

      {showBlocks && (
        <form.Field name="unavailability">
          {(field) => (
            <Field
              label="Blocked time"
              optional
              error={firstFieldError(field.state.meta.errors)}
            >
              {() => (
                <form.Subscribe
                  selector={(s) => ({
                    mode: s.values.bookingWindowMode,
                    horizonDays: s.values.bookingHorizonDays,
                    until: s.values.bookingUntil,
                  })}
                >
                  {({ mode, horizonDays, until }) => (
                    <UnavailabilityCalendar
                      horizonDays={previewHorizonDays(mode, horizonDays, until)}
                      value={field.state.value}
                      onChange={(blocks) => field.handleChange(blocks)}
                    />
                  )}
                </form.Subscribe>
              )}
            </Field>
          )}
        </form.Field>
      )}
    </Stack>
  );
}

function DaysField({ form }: { form: NewListingFormApi }) {
  return (
    <form.Field name="bookingHorizonDays">
      {(field) => (
        <Field
          label="Days ahead"
          hint={`Between ${MIN_BOOKING_HORIZON_DAYS} and ${MAX_BOOKING_HORIZON_DAYS} days (default ${DEFAULT_BOOKING_HORIZON_DAYS}). The calendar below shows this window.`}
          error={firstFieldError(field.state.meta.errors)}
        >
          {(wiring) => (
            <div className={styles.horizonRow}>
              <Input
                {...wiring}
                mono
                type="number"
                inputMode="numeric"
                min={MIN_BOOKING_HORIZON_DAYS}
                max={MAX_BOOKING_HORIZON_DAYS}
                step={1}
                className={styles.horizonInput}
                value={Number.isFinite(field.state.value) ? field.state.value : ''}
                onBlur={field.handleBlur}
                onChange={(e) => {
                  const n = e.target.valueAsNumber;
                  field.handleChange(Number.isNaN(n) ? DEFAULT_BOOKING_HORIZON_DAYS : Math.trunc(n));
                }}
              />
              <Text as="span" tone="soft">
                days ahead
              </Text>
            </div>
          )}
        </Field>
      )}
    </form.Field>
  );
}

function DateField({ form }: { form: NewListingFormApi }) {
  return (
    <form.Field name="bookingUntil">
      {(field) => (
        <Field
          label="Last bookable date"
          hint="Agents can book through the end of this day. The calendar below shows this window."
          error={firstFieldError(field.state.meta.errors)}
        >
          {(wiring) => (
            <Input
              {...wiring}
              type="date"
              min={addDays(todayInZone(), 1)}
              className={styles.dateInput}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
          )}
        </Field>
      )}
    </form.Field>
  );
}

// ponytail: local preview math for the UNSAVED create form only: there's no server listing yet to
// read bookingWindowEnd from. Saved listings drive the grid off the server-resolved window instead.
function previewHorizonDays(mode: BookingWindowMode, horizonDays: number, until: string): number {
  if (mode === BookingWindowMode.Date) {
    const [y, m, d] = until.split('-').map(Number);
    if (y && m && d) {
      const target = new Date(y, m - 1, d);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
      if (days > 0) return days;
    }
    return DEFAULT_BOOKING_HORIZON_DAYS;
  }
  return Number.isFinite(horizonDays) && horizonDays > 0 ? horizonDays : DEFAULT_BOOKING_HORIZON_DAYS;
}
